import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import type { ExpenseSummary } from '@/types'
import { getCategoryBadge } from './expense-constants'
import { CategoryLabel, MONEY_SERIES, useMoney } from './moneyUi'
import { useExpenseCategoryDefs } from './use-expense-category-defs'

type Props = {
  /** The period's summary — the same one the ledger totals read (F-RPT-N37). */
  summary?: ExpenseSummary
  /** Category slug currently selected; '' = all. */
  selectedCategory: string
  /** Click a tile to open / filter to it; '' means "all expenses". */
  onSelectCategory: (slug: string) => void
  /** Hide categories with no entries in the period (Overview). */
  hideEmpty?: boolean
  className?: string
}

type Tile = {
  slug: string
  label: string
  color: string
  total: number
  count: number
  isStock: boolean
}

/**
 * Spend by category as a row of KPI tiles the owner can read at once: how
 * much went to salaries this period and how many entries made that number.
 * Unless `hideEmpty`, every active category renders even at zero, so what was
 * NOT recorded is as visible as what was. Tiles are doors into the ledger.
 */
export function ExpenseCategoryShelf({ summary, selectedCategory, onSelectCategory, hideEmpty = false, className }: Props) {
  const money = useMoney()
  const { data: defs = [] } = useExpenseCategoryDefs()

  const { tiles, spendSum, entryCount } = useMemo(() => {
    const bySlug = new Map<string, { count: number; total: number; stock: number }>()
    for (const row of summary?.categories ?? []) {
      bySlug.set(row.category, { count: row.count, total: row.total, stock: row.stock_total ?? 0 })
    }
    const seen = new Set<string>()
    const out: Tile[] = []
    const activeDefs = [...defs]
      .filter(d => d.is_active)
      .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label))
    for (const d of activeDefs) {
      const hit = bySlug.get(d.slug)
      seen.add(d.slug)
      out.push({
        slug: d.slug,
        label: d.label,
        color: d.color,
        total: hit?.total ?? 0,
        count: hit?.count ?? 0,
        isStock: (hit?.stock ?? 0) > 0.005,
      })
    }
    // Rows filed under a slug that is no longer an active definition still count.
    for (const [slug, v] of bySlug) {
      if (seen.has(slug)) continue
      const b = getCategoryBadge(slug, defs)
      out.push({ slug, label: b.label, color: b.color, total: v.total, count: v.count, isStock: v.stock > 0.005 })
    }
    out.sort((a, b) => b.total - a.total || b.count - a.count || a.label.localeCompare(b.label))
    // Share is of all positive spend in the period (stock included), never of
    // opex alone — a stock category would otherwise read as more than 100%.
    const sum = out.reduce((s, t) => (t.total > 0 ? s + t.total : s), 0)
    const entries = summary?.count ?? out.reduce((s, t) => s + t.count, 0)
    return { tiles: hideEmpty ? out.filter(t => t.count > 0) : out, spendSum: sum, entryCount: entries }
  }, [summary, defs, hideEmpty])

  const opex = summary?.grand_total ?? 0
  const stock = summary?.stock_total ?? 0
  const allSelected = selectedCategory === ''

  const tileClass = (selected: boolean, dim = false) =>
    cn(
      'flex flex-col rounded-lg border bg-card p-4 text-left transition-colors',
      selected ? 'border-foreground/60 ring-1 ring-foreground/60' : 'hover:bg-muted/40',
      dim && !selected && 'opacity-70',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    )

  return (
    <div className={cn('grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6', className)}>
      <button type="button" aria-pressed={allSelected} onClick={() => onSelectCategory('')} className={tileClass(allSelected)}>
        <span className="text-sm text-muted-foreground">All operating expenses</span>
        <span className="mt-1 text-xl font-semibold tracking-tight" title={money.full(opex)}>
          {money.whole(opex)}
        </span>
        <span className="mt-1 text-xs text-muted-foreground">{entryCount === 1 ? '1 entry' : `${entryCount} entries`}</span>
        {stock > 0.005 && <span className="mt-auto pt-2 text-xs text-muted-foreground">plus {money.whole(stock)} of stock</span>}
      </button>

      {tiles.map(t => {
        const selected = selectedCategory === t.slug
        const share = spendSum > 0 && t.total > 0 ? Math.min(100, (t.total / spendSum) * 100) : 0
        const isCredit = t.total < 0
        return (
          <button
            key={t.slug}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelectCategory(selected ? '' : t.slug)}
            className={tileClass(selected, t.count === 0)}
          >
            <span className="flex min-w-0 items-center gap-2 text-sm">
              <CategoryLabel label={t.label} color={t.color} className="[&>span:last-child]:text-muted-foreground" />
              {t.isStock && <span className="shrink-0 text-xs text-muted-foreground">stock</span>}
            </span>
            <span className="mt-1 text-xl font-semibold tracking-tight" title={money.full(t.total)}>
              {money.whole(t.total)}
            </span>
            <span className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{t.count === 1 ? '1 entry' : `${t.count} entries`}</span>
              {isCredit ? (
                <span className="text-success-fg">credit</span>
              ) : (
                t.total > 0 && <span className="tabular-nums">{share < 1 ? 'under 1' : Math.round(share)}%</span>
              )}
            </span>
            {/* One colour for every bar: the dot names the category, the bar
                only has to say how big. Stock is cost of goods, so it takes
                that colour rather than the operating-expense one. */}
            <span className="mt-2 h-1 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
              <span
                className="block h-full rounded-full"
                style={{ width: `${share}%`, background: t.isStock ? MONEY_SERIES.cogs : MONEY_SERIES.opex }}
              />
            </span>
          </button>
        )
      })}
    </div>
  )
}
